import { ref, computed } from 'vue'
import type { ChatAgent, AgentMessage } from '@/types/agentChat'
import {
  fetchChatAgents,
  fetchAgentMessages,
  fetchAgentUnreadCounts,
  sendAgentMessage,
  markAgentMessagesRead,
} from '@/services/agentChatService'
import { showPushNotification } from '@/services/notifications'

const POLL_MS = 15_000

// Estado de módulo (singleton) — mesmo padrão de useFloatChats/useAdmin:
// chamar useAgentChat() em vários componentes reusa a mesma instância.
const agents         = ref<ChatAgent[]>([])
const unreadByAgent  = ref<Record<number, number>>({})
const messagesByAgent = ref<Record<number, AgentMessage[]>>({})
const activeAgentId  = ref<number | null>(null)
const panelOpen       = ref(false)
const loadingAgents   = ref(false)
const loadingMessages = ref(false)
const sendError        = ref('')

let pollTimer: ReturnType<typeof setInterval> | null = null
let pollToken = ''
let pollUserId = 0
// Evita notificar tudo que já estava não lido de sessões anteriores no
// primeiro loadUnread() do carregamento — só o que aumentar A PARTIR daqui.
let hasLoadedUnreadOnce = false

// Mesmo critério do resto do app (nenhuma notificação nativa dispara com a
// aba em foco — o badge dentro da UI já é suficiente nesse caso).
function isTabFocused(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus()
}

function agentName(id: number): string {
  return agents.value.find(a => a.id === id)?.name ?? 'Um agente'
}

export function useAgentChat() {
  const totalUnread = computed(() =>
    Object.values(unreadByAgent.value).reduce((s, n) => s + n, 0)
  )

  const activeAgent = computed(() =>
    agents.value.find(a => a.id === activeAgentId.value) ?? null
  )

  const activeMessages = computed(() =>
    activeAgentId.value != null ? (messagesByAgent.value[activeAgentId.value] ?? []) : []
  )

  async function loadAgents(token: string, myId: number) {
    if (loadingAgents.value) return
    loadingAgents.value = true
    try {
      agents.value = await fetchChatAgents(token, myId)
    } catch (err) {
      console.warn('[AgentChat] loadAgents:', err)
    } finally {
      loadingAgents.value = false
    }
  }

  async function loadUnread(myId: number) {
    try {
      const rows = await fetchAgentUnreadCounts(myId)
      const map: Record<number, number> = {}
      rows.forEach(r => { map[r.senderId] = r.unread })

      // A thread ativa é notificada com o conteúdo real pelo poll() (thread
      // branch, abaixo) — pular aqui pra não duplicar com um aviso genérico.
      if (hasLoadedUnreadOnce && !isTabFocused()) {
        for (const [idStr, count] of Object.entries(map)) {
          const senderId = Number(idStr)
          const prev = unreadByAgent.value[senderId] ?? 0
          if (count > prev && senderId !== activeAgentId.value) {
            showPushNotification(
              agentName(senderId),
              'Nova mensagem no chat entre agentes',
              window.location.href,
              `agent-chat-${senderId}`,
              undefined,
              { data: { type: 'agent-chat', senderId } },
            )
          }
        }
      }

      unreadByAgent.value = map
      hasLoadedUnreadOnce = true
    } catch (err) {
      console.warn('[AgentChat] loadUnread:', err)
    }
  }

  async function openThread(myId: number, otherId: number) {
    activeAgentId.value = otherId
    loadingMessages.value = true
    try {
      messagesByAgent.value[otherId] = await fetchAgentMessages(myId, otherId)
      await markAgentMessagesRead(myId, otherId)
      unreadByAgent.value = { ...unreadByAgent.value, [otherId]: 0 }
    } catch (err) {
      console.warn('[AgentChat] openThread:', err)
    } finally {
      loadingMessages.value = false
    }
  }

  function backToList() {
    activeAgentId.value = null
  }

  function togglePanel() {
    panelOpen.value = !panelOpen.value
    if (!panelOpen.value) activeAgentId.value = null
  }

  function closePanel() {
    panelOpen.value = false
    activeAgentId.value = null
  }

  async function send(myId: number, otherId: number, body: string) {
    const text = body.trim()
    if (!text) return
    sendError.value = ''
    try {
      await sendAgentMessage(myId, otherId, text)
      const lastId = messagesByAgent.value[otherId]?.slice(-1)[0]?.id
      const fresh = await fetchAgentMessages(myId, otherId, lastId)
      const current = messagesByAgent.value[otherId] ?? []
      messagesByAgent.value = { ...messagesByAgent.value, [otherId]: [...current, ...fresh] }
    } catch (err) {
      console.warn('[AgentChat] send:', err)
      sendError.value = 'Não foi possível enviar a mensagem.'
    }
  }

  // Polling leve (sem backoff/circuito) — só cobre a thread aberta (mensagens
  // novas dela). list_agent_unread_counts NÃO roda mais aqui: passou a rodar
  // só junto do ciclo do Dashboard (login/clique em Atualizar/fim do
  // countdown — ver useDashboardEngine.loadData()), pra não bater no n8n a
  // cada 15s independente disso. Ainda assim, sempre 1 timer só (guardas por
  // token/userId) e try/catch em toda chamada — uma falha de rede não deve
  // virar timer duplicado nem exception não tratada.
  async function poll() {
    if (!pollToken || !pollUserId) return
    if (activeAgentId.value != null) {
      const otherId = activeAgentId.value
      try {
        const current = messagesByAgent.value[otherId] ?? []
        const lastId = current.slice(-1)[0]?.id
        const fresh = await fetchAgentMessages(pollUserId, otherId, lastId)
        if (fresh.length) {
          messagesByAgent.value = { ...messagesByAgent.value, [otherId]: [...current, ...fresh] }
          // Só mensagens de quem não é a gente mesmo — evita notificar o
          // próprio envio (send() já faz esse mesmo fetch incremental).
          const fromOther = fresh.filter(m => m.senderId === otherId)
          if (fromOther.length && !isTabFocused()) {
            const last = fromOther[fromOther.length - 1]
            showPushNotification(
              agentName(otherId),
              last.body,
              window.location.href,
              `agent-chat-${otherId}`,
              undefined,
              { data: { type: 'agent-chat', senderId: otherId } },
            )
          }
          await markAgentMessagesRead(pollUserId, otherId)
          unreadByAgent.value = { ...unreadByAgent.value, [otherId]: 0 }
        }
      } catch (err) {
        console.warn('[AgentChat] poll (thread):', err)
      }
    }
  }

  function startPolling(token: string, myId: number) {
    pollToken = token
    pollUserId = myId
    if (pollTimer) return // já rodando — evita timer duplicado em remount
    pollTimer = setInterval(poll, POLL_MS)
  }

  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    pollToken = ''
    pollUserId = 0
  }

  // Chamado no logout (performLogout) — estado de módulo sobrevive à troca de
  // rota, sem isso a lista/mensagens do agente anterior apareceriam por um
  // instante pro próximo login na mesma aba. Mesmo motivo do useFloatChats.closeAll().
  function reset() {
    stopPolling()
    agents.value = []
    unreadByAgent.value = {}
    messagesByAgent.value = {}
    activeAgentId.value = null
    panelOpen.value = false
    sendError.value = ''
    hasLoadedUnreadOnce = false
  }

  return {
    agents,
    unreadByAgent,
    totalUnread,
    activeAgentId,
    activeAgent,
    activeMessages,
    panelOpen,
    loadingAgents,
    loadingMessages,
    sendError,
    loadAgents,
    loadUnread,
    openThread,
    backToList,
    togglePanel,
    closePanel,
    send,
    startPolling,
    stopPolling,
    reset,
  }
}
